"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  NumberResult,
  type ResultLine,
} from "@/components/tools/numbers-languages/number-result";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type DayOfYear,
  ERAS,
  type EraDate,
  type EraId,
  type EraNote,
  type EraSpan,
  eraById,
  eraDateKanji,
  eraDateShort,
  eraDateSquare,
  eraLastYear,
  eraYearKanji,
  eraYearSpan,
  eraYearsOf,
  type FromEra,
  fromEra,
  toEra,
  toIso,
} from "@/lib/tool-kit/numbers-languages/japanese-era";

/** English strings. A translated content file overrides these through `ui`. */
export const JAPANESE_ERA_CONVERTER_STRINGS = {
  directionLabel: "Convert",
  toEra: "Gregorian → Japanese era",
  fromEra: "Japanese era → Gregorian",
  yearLabel: "Year",
  monthLabel: "Month (optional)",
  dayLabel: "Day (optional)",
  yearHint:
    "A Gregorian year from 1868. Leave month and day empty for the whole year.",
  eraLabel: "Era",
  eraYearLabel: "Era year",
  eraYearHint: "A number, or 元 for the first year (gannen).",
  gannen: "Write the first year as 元 (gannen), not 1",
  resultHeading: "Japanese era date",
  yearResultHeading: "Eras in this year",
  gregorianHeading: "Gregorian date",
  gregorianYearHeading: "Gregorian year",
  copy: "Copy",
  copied: "Copied!",
  compactLabel: "Compact",
  squareLabel: "Square glyph",
  englishLabel: "Gregorian",
  isoLabel: "ISO 8601",
  yearsLabel: "Days covered",
  spanLabel: "{era} {year}",
  rulesHeading: "Notes",
  enterPrompt: "Enter a date to convert it.",
  errorInvalid: "That is not a valid date.",
  errorPartial: "Enter both the month and the day, or leave both empty.",
  errorBefore:
    "The Meiji era began on 23 October 1868. Earlier dates belong to Keiō and older eras, which this tool does not cover.",
  errorOutside:
    "That date is outside {era} {year}, which runs from {from} to {to}.",
  errorNoYear: "{era} has no year {year}: it ended in {last}, its last year.",
  errorEraYear: "Enter a whole number of 1 or more for the era year, or 元.",
  noteGannen:
    "The first year of an era is called 元年 (gannen) and runs from the start date to 31 December.",
  noteEraStart: "This is the first day of the era.",
  noteEraEnd: "This is the last day of the era.",
  noteSharedYear:
    "This Gregorian year holds an era change, so it belongs to two eras (for example 1989 is Shōwa 64 and Heisei 1).",
  noteLunisolar:
    "Before 1 January 1873 Japan used a lunisolar calendar, and the era year changed at the old New Year in January or February. Treat the era year as approximate.",
  noteFirstGregorian:
    "Japan adopted the Gregorian calendar on this day; it followed 2 December Meiji 5 in the old calendar.",
} as const;

type Strings = typeof JAPANESE_ERA_CONVERTER_STRINGS;
type Direction = "toEra" | "fromEra";

const NOTE_KEYS: Record<EraNote, keyof Strings> = {
  gannen: "noteGannen",
  eraStart: "noteEraStart",
  eraEnd: "noteEraEnd",
  sharedYear: "noteSharedYear",
  lunisolar: "noteLunisolar",
  firstGregorian: "noteFirstGregorian",
};

interface View {
  words: string;
  heading: string;
  lang: string;
  lines: ResultLine[];
  notes: string[];
}

type Outcome = { view: View } | { error: string } | null;

interface Fields {
  year: string;
  month: string;
  day: string;
  era: EraId;
  eraYear: string;
  gannen: boolean;
}

interface Context {
  locale: string;
  strings: Strings;
}

function blank(value: string): boolean {
  return value.trim() === "";
}

function longDate(locale: string, date: DayOfYear): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "long",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(date.year, date.month - 1, date.day)));
}

function dateLines(
  result: EraDate,
  { locale, strings }: Context
): ResultLine[] {
  const { era, year, date } = result;
  return [
    { label: strings.compactLabel, text: eraDateShort(era, year, date) },
    {
      label: strings.squareLabel,
      text: eraDateSquare(era, year, date),
      lang: "ja",
    },
    { label: strings.englishLabel, text: longDate(locale, date), lang: locale },
    { label: strings.isoLabel, text: toIso(date) },
  ];
}

function spansOutcome(
  spans: EraSpan[],
  gannen: boolean,
  { locale, strings }: Context
): Outcome {
  const lines: ResultLine[] = spans.map((s) => ({
    label: t(strings.spanLabel, { era: s.era.romaji, year: String(s.year) }),
    text: `${longDate(locale, s.from)} – ${longDate(locale, s.to)}`,
    lang: locale,
  }));
  const notes: string[] = [];
  if (spans.length > 1) {
    notes.push(strings.noteSharedYear);
  }
  if (spans.some((s) => s.year === 1)) {
    notes.push(strings.noteGannen);
  }
  if (spans.some((s) => s.era.id === "meiji" && s.year <= 5)) {
    notes.push(strings.noteLunisolar);
  }
  return {
    view: {
      words: spans.map((s) => eraYearKanji(s.era, s.year, gannen)).join(" / "),
      heading: strings.yearResultHeading,
      lang: "ja",
      lines,
      notes,
    },
  };
}

/** Month and day, both or neither: a pair of numbers, null for "not given", or an error. */
function readMonthDay(
  f: Fields,
  strings: Strings
): { month: number; day: number } | null | { error: string } {
  if (blank(f.month) && blank(f.day)) {
    return null;
  }
  if (blank(f.month) || blank(f.day)) {
    return { error: strings.errorPartial };
  }
  const month = parseIntegerInput(f.month);
  const day = parseIntegerInput(f.day);
  return month === null || day === null
    ? { error: strings.errorInvalid }
    : { month, day };
}

function gregorianToEra(f: Fields, ctx: Context): Outcome {
  const { strings } = ctx;
  if (blank(f.year)) {
    return null;
  }
  const year = parseIntegerInput(f.year);
  const md = readMonthDay(f, strings);
  if (year === null || (md !== null && "error" in md)) {
    return {
      error: md !== null && "error" in md ? md.error : strings.errorInvalid,
    };
  }
  if (md === null) {
    const spans = eraYearsOf(year);
    if (spans === "beforeMeiji") {
      return { error: strings.errorBefore };
    }
    return typeof spans === "string"
      ? { error: strings.errorInvalid }
      : spansOutcome(spans, f.gannen, ctx);
  }
  const result = toEra({ year, month: md.month, day: md.day });
  if (result === "beforeMeiji") {
    return { error: strings.errorBefore };
  }
  if (result === "invalid") {
    return { error: strings.errorInvalid };
  }
  return {
    view: {
      words: eraDateKanji(result.era, result.year, result.date, f.gannen),
      heading: strings.resultHeading,
      lang: "ja",
      lines: dateLines(result, ctx),
      notes: result.notes.map((note) => strings[NOTE_KEYS[note]]),
    },
  };
}

function readEraYear(raw: string): number | null {
  return raw.trim() === "元" ? 1 : parseIntegerInput(raw);
}

function outsideEraMessage(
  eraId: EraId,
  year: number,
  { locale, strings }: Context
): string {
  const era = eraById(eraId);
  const span = eraYearSpan(era, year);
  if (span === null) {
    return t(strings.errorNoYear, {
      era: era.romaji,
      year: String(year),
      last: String(eraLastYear(era)),
    });
  }
  return t(strings.errorOutside, {
    era: era.romaji,
    year: String(year),
    from: longDate(locale, span.from),
    to: longDate(locale, span.to),
  });
}

function gregorianView(result: FromEra, { locale, strings }: Context): Outcome {
  const { era, year, date, gregorianYear } = result;
  const notes: string[] = [];
  if (year === 1) {
    notes.push(strings.noteGannen);
  }
  if (era.id === "meiji" && year <= 5) {
    notes.push(strings.noteLunisolar);
  }
  const lines: ResultLine[] = [
    {
      label: strings.yearsLabel,
      text: `${longDate(locale, result.from)} – ${longDate(locale, result.to)}`,
      lang: locale,
    },
  ];
  if (date !== null) {
    lines.unshift(
      { label: strings.compactLabel, text: eraDateShort(era, year, date) },
      { label: strings.isoLabel, text: toIso(date) }
    );
  }
  return {
    view: {
      words: date === null ? String(gregorianYear) : longDate(locale, date),
      heading:
        date === null ? strings.gregorianYearHeading : strings.gregorianHeading,
      lang: locale,
      lines,
      notes,
    },
  };
}

function eraToGregorian(f: Fields, ctx: Context): Outcome {
  const { strings } = ctx;
  if (blank(f.eraYear)) {
    return null;
  }
  const year = readEraYear(f.eraYear);
  if (year === null || year < 1) {
    return { error: strings.errorEraYear };
  }
  const md = readMonthDay(f, strings);
  if (md !== null && "error" in md) {
    return { error: md.error };
  }
  const result = fromEra(f.era, year, md?.month ?? null, md?.day ?? null);
  if (result === "invalid") {
    return { error: strings.errorInvalid };
  }
  if (result === "outsideEra") {
    return { error: outsideEraMessage(f.era, year, ctx) };
  }
  return gregorianView(result, ctx);
}

export default function JapaneseEraConverterWidget() {
  const strings = useToolUi(JAPANESE_ERA_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const [direction, setDirection] = useState<Direction>("toEra");
  const [fields, setFields] = useState<Fields>({
    year: "1989",
    month: "1",
    day: "8",
    era: "heisei",
    eraYear: "元",
    gannen: true,
  });
  const set = <K extends keyof Fields>(key: K, value: Fields[K]) =>
    setFields((current) => ({ ...current, [key]: value }));
  const forward = direction === "toEra";
  const ctx: Context = { locale, strings };
  const outcome = forward
    ? gregorianToEra(fields, ctx)
    : eraToGregorian(fields, ctx);

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.directionLabel}
        name="je-direction"
        onChange={setDirection}
        options={[
          { value: "toEra", label: strings.toEra },
          { value: "fromEra", label: strings.fromEra },
        ]}
        value={direction}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        {forward ? (
          <TextField
            hint={strings.yearHint}
            id="je-year"
            label={strings.yearLabel}
            numeric
            onChange={(value) => set("year", value)}
            value={fields.year}
          />
        ) : (
          <>
            <SelectField
              id="je-era"
              label={strings.eraLabel}
              onChange={(value) => set("era", value)}
              options={ERAS.map((era) => ({
                value: era.id,
                label: `${era.kanji} ${era.romaji}`,
              }))}
              value={fields.era}
            />
            <TextField
              hint={strings.eraYearHint}
              id="je-era-year"
              label={strings.eraYearLabel}
              onChange={(value) => set("eraYear", value)}
              value={fields.eraYear}
            />
          </>
        )}
        <TextField
          id="je-month"
          label={strings.monthLabel}
          numeric
          onChange={(value) => set("month", value)}
          value={fields.month}
        />
        <TextField
          id="je-day"
          label={strings.dayLabel}
          numeric
          onChange={(value) => set("day", value)}
          value={fields.day}
        />
      </div>
      <Checkbox
        checked={fields.gannen}
        label={strings.gannen}
        onChange={(value) => set("gannen", value)}
      />
      {outcome !== null && "error" in outcome ? (
        <ErrorNotice>{outcome.error}</ErrorNotice>
      ) : null}
      {outcome !== null && "view" in outcome ? (
        <NumberResult
          lang={outcome.view.lang}
          lines={outcome.view.lines}
          notes={outcome.view.notes}
          strings={{
            heading: outcome.view.heading,
            copy: strings.copy,
            copied: strings.copied,
            rulesHeading: strings.rulesHeading,
          }}
          words={outcome.view.words}
        />
      ) : null}
      {outcome === null ? <Notice>{strings.enterPrompt}</Notice> : null}
    </div>
  );
}
